import type { NavigateFunction } from "react-router-dom";
// Material UI
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import IconButton from "@mui/material/IconButton";
import { formatarDataMovimentacaoFinanceiro } from "../dashboard/helpers/formatar-data-financeiro";
// Interfaces
import type { ILancamentoFinanceiroRow } from "interfaces/loja/financeiro";

const brl = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

function labelTipo(t: string) {
  if (t === "RECEITA") return "Receita";
  if (t === "DESPESA") return "Despesa";
  return t;
}

function labelOrigem(o: string) {
  const map: Record<string, string> = {
    PEDIDO: "Pedido (legado)",
    VENDA: "Venda",
    FRETE: "Frete",
    TAXA_GATEWAY: "Taxa gateway",
    ESTORNO: "Estorno",
    AJUSTE: "Ajuste",
  };
  return map[o] ?? o;
}

function labelStatus(s: string) {
  const map: Record<string, string> = {
    PENDENTE: "Pendente",
    CONFIRMADO: "Confirmado",
    CANCELADO: "Cancelado",
  };
  return map[s] ?? s;
}

export function createFinanceiroColumns(
  navigate: NavigateFunction
): GridColDef<ILancamentoFinanceiroRow>[] {
  return [
    {
      field: "dataCompetencia",
      headerName: "Competência",
      flex: 1,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams<ILancamentoFinanceiroRow>) => (
        <Typography variant="body2">
          {params.row.dataCompetencia
            ? formatarDataMovimentacaoFinanceiro(params.row.dataCompetencia)
            : "—"}
        </Typography>
      ),
    },
    {
      field: "dataEfetiva",
      headerName: "Caixa",
      flex: 1,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams<ILancamentoFinanceiroRow>) => (
        <Typography variant="body2">
          {params.row.dataEfetiva
            ? formatarDataMovimentacaoFinanceiro(params.row.dataEfetiva)
            : "—"}
        </Typography>
      ),
    },
    {
      field: "tipo",
      headerName: "Tipo",
      flex: 0.7,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams<ILancamentoFinanceiroRow>) => {
        const isRec = params.row.tipo === "RECEITA";
        return (
          <Chip
            size="small"
            label={labelTipo(params.row.tipo)}
            color={isRec ? "success" : "error"}
            variant="outlined"
          />
        );
      },
    },
    {
      field: "origem",
      headerName: "Origem",
      flex: 0.9,
      minWidth: 120,
      valueGetter: (params) => labelOrigem(params.row.origem),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 110,
      valueGetter: (params) => labelStatus(params.row.status),
    },
    {
      field: "valor",
      headerName: "Valor",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<ILancamentoFinanceiroRow>) => (
        <Typography variant="body2" fontWeight={600}>
          {brl.format(Number(params.row.valor))}
        </Typography>
      ),
    },
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 0.5,
      minWidth: 160,
      sortable: false,
      valueGetter: (params) => params.row.descricao ?? "—",
    },
    {
      field: "pedidoId",
      headerName: "Pedido",
      align: "center",
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ILancamentoFinanceiroRow>) => {
        const id = params.row.pedidoId;
        if (!id) return <Typography variant="body2">—</Typography>;
        return (
          <IconButton
            onClick={() => navigate(`/loja/pedidos/${id}`)}
            color="primary"
          >
            <AssignmentIcon sx={{ fontSize: 36 }} />
          </IconButton>
        );
      },
    },
    {
      field: "pagamentoId",
      headerName: "Pagamento",
      align: "center",
      headerAlign: "center",
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      valueGetter: (params) =>
        params.row.pagamentoId
          ? `${params.row.pagamentoId.slice(0, 10)}…`
          : "—",
      renderCell: (params: GridRenderCellParams<ILancamentoFinanceiroRow>) => {
        const id = params.row.pagamentoId;
        if (!id) return <Typography variant="body2">—</Typography>;
        return (
          <IconButton
            onClick={() => navigate(`/loja/pagamentos/${id}`)}
            color="primary"
          >
            <CreditScoreIcon sx={{ fontSize: 36 }} color="success" />
          </IconButton>
        );
      },
    },
    {
      field: "metodoPagamento",
      headerName: "Método",
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      valueGetter: (params) => params.row.metodoPagamento ?? "—",
    },
  ];
}
